// Copyright (c) 2026 Michael F. Kamprath
//
// quiz-plaque-manager.js -- Orchestrates the choice plaques for one
// quiz round.  Implements the QuizPlaqueManager interface that
// createQuizModeMachine (src/game/quiz-mode.js) drives:
//
//   addPlaques(correctValue, token) -> Promise<void>
//   onTap(cb)                       -> () => void (unsubscribe)
//   playCorrectAnimation(plaqueId)  -> Promise<void>
//   playIncorrectAnimation(plaqueId) -> Promise<void>
//   dimAllUnselected()              -> Promise<void>
//   destroy()                       -> void
//
// Responsibilities not handled by the renderer (src/game/choice-plaque.js)
// or the state machine:
//   - Value generation per QUIZ-MODE-LEGACY-SPEC §3 + §11 bonus
//     correction (seed with correctValue, fill with distinct random
//     wrongs, then pop random assignments from the pool into
//     locationList slots).
//   - Staggered entrance (0.05 s inter-plaque delay) with indexed
//     pop sounds via gameAudio.playPlaquePop.
//   - Per-plaque interactivity: pointerdown starts a touch-in
//     animation + plays an indexed plaqueTouch pop; pointerup
//     emits the tap event upstream.  The manager enforces the
//     `selected` latch (§4): a tapped plaque no longer fires
//     further tap events.
//   - Per-plaque animation lifecycle: a currentCancel token is
//     tripped whenever a new animation is started so in-flight
//     animations read current scale/alpha as their starting point
//     (touchIn interrupts touchOut, incorrect interrupts touchIn,
//     correct interrupts touchIn, etc.) -- matches the legacy
//     animateWithDuration: replacement behaviour.
//
// PixiJS is used directly here (hit-testing, event mode, adding
// plaques to the choice-plaque layer).  For unit tests the module
// accepts a mocked pixi.js via vi.mock at the test level; for
// integration use the real PIXI.Container / Sprite / Text.

import { Rectangle } from 'pixi.js';
import {
  createChoicePlaque,
  quizPlaqueScales,
  animateChoicePlaqueEntrance,
  animateChoicePlaqueTouchIn,
  animateChoicePlaqueTouchOut,
  animateChoicePlaqueCorrect,
  animateChoicePlaqueIncorrect,
} from './choice-plaque.js';

// --- Constants -------------------------------------------------------------

/**
 * Default stagger between successive plaque entrances (50 ms matches
 * legacy spec §3, CountGameController.m:412).  Kept as integer ms
 * instead of 0.05 s so that `i * INTER_PLAQUE_DELAY_MS` doesn't hit
 * IEEE-754 imprecision (0.05 × 3 === 0.15000000000000002 in JS) -- a
 * rounded-up fireAt fails the fake clock's `fireAt <= t` check in
 * tests.  Callers can override via the `tuning.interPlaqueDelayMs`
 * constructor option.
 */
const DEFAULT_INTER_PLAQUE_DELAY_MS = 50;

/** Max distinct pop-touch sounds -- the legacy plaqueTouchSequence has 4 entries. */
const MAX_TOUCH_POP_INDEX = 3;

/**
 * Percent strings ("21%") or raw numbers are both supported for position
 * values.  Percentages resolve against `design` (either designW or designH).
 */
function resolvePosition(value, design) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    const pct = parseFloat(value);
    if (Number.isFinite(pct)) return (pct / 100) * design;
  }
  return 0;
}

// --- Value generation ------------------------------------------------------

/**
 * Build the value list for a round's plaques.  Exported for unit tests.
 *
 * Follows QUIZ-MODE-LEGACY-SPEC §11 "Bonus correction: value-generation
 * algorithm":
 *
 *   1. Source pool = [1..maxCount] \ {correctValue}
 *   2. Seed the value list with [correctValue]; fill with random
 *      distinct picks from the source pool until list length equals
 *      plaqueCount.
 *   3. Iterate plaqueLocations in order; pop a random value from the
 *      list and assign it to that location.
 *
 * Net effect: one correct plaque + (plaqueCount - 1) distinct wrong
 * values in [1..maxCount], randomly placed across the location slots.
 * Wrong values CAN be adjacent to the correct value (no ±1 exclusion
 * per §9).
 *
 * @param {number} correctValue
 * @param {number} maxCount
 * @param {number} plaqueCount
 * @param {() => number} rng
 * @returns {number[]}
 */
export function generatePlaqueValues(correctValue, maxCount, plaqueCount, rng = Math.random) {
  // Step 1: source pool excluding correctValue.
  const source = [];
  for (let i = 1; i <= maxCount; i++) {
    if (i !== correctValue) source.push(i);
  }

  // Step 2: seed with correctValue, fill with distinct wrongs.
  const pool = [correctValue];
  const wantedWrongs = Math.max(0, plaqueCount - 1);
  for (let j = 0; j < wantedWrongs && source.length > 0; j++) {
    const idx = Math.floor(rng() * source.length);
    pool.push(source[idx]);
    source.splice(idx, 1);
  }

  // Step 3: pop random from pool into each location slot.
  const assigned = [];
  const slots = Math.min(plaqueCount, pool.length);
  for (let loc = 0; loc < slots; loc++) {
    const idx = Math.floor(rng() * pool.length);
    assigned.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return assigned;
}

// --- Manager ---------------------------------------------------------------

/**
 * @typedef {Object} QuizPlaqueManager
 * @property {(correctValue: number, token: object) => Promise<void>} addPlaques
 * @property {(cb: (evt: { plaqueId: string, value: number, correct: boolean }) => void) => () => void} onTap
 * @property {(plaqueId: string) => Promise<void>} playCorrectAnimation
 * @property {(plaqueId: string) => Promise<void>} playIncorrectAnimation
 * @property {() => Promise<void>} dimAllUnselected
 * @property {() => void} destroy
 * @property {() => ReadonlyArray<{ id: string, value: number, isCorrect: boolean, selected: boolean }>} __getPlaques
 */

/**
 * Build a plaque manager for one quiz round.
 *
 * @param {object} opts
 * @param {*}      opts.layer        PIXI Container the plaques are attached to
 *                                   (typically scene.choicePlaqueContainer).
 * @param {object} opts.theme        Theme entry (reads theme.quizPlaque).
 * @param {Map|Object} [opts.textureMap]
 *   Pre-loaded textures keyed by image path (standardImage +
 *   incorrectImage).  Missing entries resolve to PIXI.Texture.EMPTY.
 * @param {object} opts.gameAudio    createGameAudio()'s returned object.
 *                                   Must expose playPlaquePop and
 *                                   playPlaqueTouchPop.
 * @param {number} opts.designW
 * @param {number} opts.designH
 * @param {number} opts.maxCount
 *   Upper bound of the value pool for the round (inclusive; wrong
 *   values are drawn from [1..maxCount] \ {correctValue}).  Typically
 *   comes from the app's getMaxCount() setting.  Required for the
 *   comparator audio to be able to pick "there are fewer" when the
 *   user taps a wrong plaque with value > correctValue (which requires
 *   wrong values to be able to exceed correctValue in the first place).
 * @param {{
 *   interPlaqueDelayMs?: number,
 *   entrancePhase1Ms?: number,
 *   entrancePhase2Ms?: number,
 *   scales?: {
 *     display?: number | null,
 *     touch?: number | null,
 *     correct?: number | null,
 *     incorrect?: number | null,
 *   },
 *   fontSizeMultiplier?: number | null,
 * }} [opts.tuning]
 *   Optional timing + scale + font overrides.  Timing defaults match
 *   the legacy spec (50 / 200 / 100 ms).  `scales.*` and
 *   `fontSizeMultiplier` values (when non-null) override the
 *   corresponding theme.quizPlaque fields uniformly.
 *
 *   fontSizeMultiplier precedence (first non-null wins):
 *     1. theme.quizPlaque.fontSizeMultiplier  (per-theme)
 *     2. tuning.fontSizeMultiplier            (app-config global)
 *     3. 0.80                                 (baked-in legacy default)
 *
 *   Typically sourced from getQuizTuning(app-config) so tunings can
 *   be adjusted in app-config.json without a rebuild.
 * @param {string} [opts.numeralScript]  Defaults 'arabic'.
 * @param {string} [opts.fontFamily]
 * @param {() => number} [opts.rng]     Defaults Math.random.
 * @returns {QuizPlaqueManager}
 */
export function createQuizPlaqueManager({
  layer,
  theme,
  textureMap = null,
  gameAudio,
  designW,
  designH,
  maxCount,
  tuning = {},
  numeralScript = 'arabic',
  fontFamily,
  rng = Math.random,
}) {
  if (!layer) throw new TypeError('createQuizPlaqueManager: layer required');
  if (!theme?.quizPlaque) {
    throw new TypeError('createQuizPlaqueManager: theme.quizPlaque required');
  }
  if (!gameAudio) throw new TypeError('createQuizPlaqueManager: gameAudio required');
  if (!Number.isFinite(maxCount) || maxCount < 1) {
    throw new TypeError('createQuizPlaqueManager: maxCount (>= 1) required');
  }

  // Resolve timing with per-field fallback so a partial `tuning`
  // object still works.  Negative / non-finite values silently fall
  // back to defaults (defensive -- callers shouldn't need to sanitise
  // before passing through).
  const finiteMs = (v, fb) => (Number.isFinite(v) && v >= 0 ? v : fb);
  const interPlaqueDelayMs = finiteMs(tuning.interPlaqueDelayMs, DEFAULT_INTER_PLAQUE_DELAY_MS);
  const entrancePhase1Ms = finiteMs(tuning.entrancePhase1Ms, 200);
  const entrancePhase2Ms = finiteMs(tuning.entrancePhase2Ms, 100);

  // Merge scale overrides into a derived cfg so every downstream
  // consumer (createChoicePlaque + the 5 animators) sees one coherent
  // source of truth.  Nullish coalescing means null / undefined /
  // missing falls through to the per-theme value; 0 and other falsy
  // numbers are honoured as explicit overrides (`?? cfg.displayScale`
  // would treat 0 as unset; we deliberately use ?? so the
  // configurable value wins when it's a real number).
  const scaleOverrides = tuning.scales || {};

  // fontSizeMultiplier precedence: per-theme > global > baked 0.80.
  // Each layer's value is validated here (positive finite number);
  // invalid values silently fall through to the next layer so a
  // typo in themes.json doesn't silently produce 0-sized numerals.
  const validMult = (v) => (Number.isFinite(v) && v > 0 ? v : null);
  const resolvedFontMult =
    validMult(theme.quizPlaque.fontSizeMultiplier)
    ?? validMult(tuning.fontSizeMultiplier)
    ?? 0.80;

  const cfg = {
    ...theme.quizPlaque,
    displayScale:   scaleOverrides.display   ?? theme.quizPlaque.displayScale,
    touchScale:     scaleOverrides.touch     ?? theme.quizPlaque.touchScale,
    correctScale:   scaleOverrides.correct   ?? theme.quizPlaque.correctScale,
    incorrectScale: scaleOverrides.incorrect ?? theme.quizPlaque.incorrectScale,
    fontSizeMultiplier: resolvedFontMult,
  };
  const locations = cfg.locationList || [];
  const plaqueCount = locations.length;
  const { display } = quizPlaqueScales(cfg);

  /**
   * @typedef {Object} PlaqueEntry
   * @property {string} id
   * @property {number} value
   * @property {boolean} isCorrect
   * @property {boolean} selected
   * @property {*} container
   * @property {{ cancelled: boolean } | null} currentCancel
   * @property {Array<{ event: string, handler: Function }>} listeners
   */
  /** @type {PlaqueEntry[]} */
  const plaques = [];

  /** @type {Set<(evt: any) => void>} */
  const subscribers = new Set();

  let destroyed = false;
  let touchCounter = 0;

  // -----------------------------------------------------------------------

  function tripCancel(entry) {
    if (entry.currentCancel) entry.currentCancel.cancelled = true;
    const next = { cancelled: false };
    entry.currentCancel = next;
    return next;
  }

  function emitTap(entry) {
    const evt = {
      plaqueId: entry.id,
      value: entry.value,
      correct: entry.isCorrect,
    };
    for (const cb of [...subscribers]) {
      try { cb(evt); }
      catch (err) {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[quiz-plaque-manager] onTap subscriber threw:', err);
        }
      }
    }
  }

  function onPointerDown(entry) {
    if (destroyed || entry.selected) return;
    // Suppress the touch-pop sound AND the counter increment when the
    // tapped plaque is the correct answer -- only the CORRECT_FEEDBACK
    // reveal SFX (playQuizCorrectReveal) should sound on a correct
    // tap.  This is a deliberate deviation from spec §4 where legacy
    // fires the pop unconditionally; UX preference is one cue per
    // correct tap, not a selection pop stepping on the reveal.  The
    // counter staying put means the next WRONG tap plays the same
    // indexed pop it would have otherwise (no gap in the sequence).
    if (!entry.isCorrect) {
      try {
        gameAudio.playPlaqueTouchPop(Math.min(touchCounter, MAX_TOUCH_POP_INDEX));
      } catch { /* audio errors never block UI */ }
      touchCounter += 1;
    }
    // Touch-in animation fires regardless -- the plaque still grows
    // visually on press so the tap feels responsive even when silent.
    const cancel = tripCancel(entry);
    animateChoicePlaqueTouchIn(entry.container, { cfg, cancel })
      .catch(() => { /* animator swallows its own errors */ });
  }

  function onPointerUp(entry) {
    if (destroyed || entry.selected) return;
    entry.selected = true;
    // Emit the tap -- the state machine will immediately call
    // playCorrectAnimation or playIncorrectAnimation, which trips the
    // in-flight touch-in via tripCancel.
    emitTap(entry);
  }

  function onPointerUpOutside(entry) {
    if (destroyed || entry.selected) return;
    // Finger released outside the plaque.  Run touch-out to return
    // scale to displayScale; no tap event fires.
    const cancel = tripCancel(entry);
    animateChoicePlaqueTouchOut(entry.container, { cfg, cancel })
      .catch(() => { /* animator swallows its own errors */ });
  }

  function wireInteractivity(entry) {
    const c = entry.container;
    c.eventMode = 'static';
    c.cursor = 'pointer';
    // Hit area centred on the container origin (which IS the plaque's
    // visual centre -- see choice-plaque.js createChoicePlaque).  We
    // size it to the resting displayScale rectangle so the clickable
    // area matches the on-screen plaque even while scale is mid-tween.
    const halfW = (cfg.imageSize.w * display) / 2;
    const halfH = (cfg.imageSize.h * display) / 2;
    if (typeof Rectangle === 'function') {
      c.hitArea = new Rectangle(-halfW, -halfH, halfW * 2, halfH * 2);
    }
    const downHandler = () => onPointerDown(entry);
    const upHandler = () => onPointerUp(entry);
    const upOutsideHandler = () => onPointerUpOutside(entry);
    c.on('pointerdown', downHandler);
    c.on('pointerup', upHandler);
    c.on('pointerupoutside', upOutsideHandler);
    entry.listeners = [
      { event: 'pointerdown', handler: downHandler },
      { event: 'pointerup', handler: upHandler },
      { event: 'pointerupoutside', handler: upOutsideHandler },
    ];
  }

  // -----------------------------------------------------------------------

  async function addPlaques(correctValue, token) {
    if (destroyed) return;
    if (plaqueCount === 0) return;

    const values = generatePlaqueValues(correctValue, maxCount, plaqueCount, rng);

    // Phase 1: build every plaque container, hide it, add to layer.
    for (let i = 0; i < plaqueCount; i++) {
      const v = values[i];
      const container = createChoicePlaque({
        cfg, value: v, textureMap, numeralScript, fontFamily,
      });
      // Position at the location's centre (locationList percentages
      // are of designW / designH).
      container.x = resolvePosition(locations[i].x, designW);
      container.y = resolvePosition(locations[i].y, designH);
      // Start invisible / scaled-to-zero so the entrance animator's
      // first write (scale 0.10, alpha 0.30) is what the user sees.
      container.alpha = 0;
      if (container.scale?.set) container.scale.set(0.01, 0.01);
      else if (container.scale) { container.scale.x = 0.01; container.scale.y = 0.01; }
      layer.addChild(container);

      const entry = {
        id: `plaque-${i}`,
        value: v,
        isCorrect: v === correctValue,
        selected: false,
        container,
        currentCancel: null,
        listeners: [],
      };
      wireInteractivity(entry);
      plaques.push(entry);
    }

    // Phase 2: staggered entrance + indexed pop sounds.  Each plaque
    // waits (i × interPlaqueDelayMs) before kicking off its entrance
    // animation.  The animations themselves run in parallel once
    // started.
    //
    // We call setTimeout directly (rather than through pauseFn) so the
    // scheduled fireAt is an integer and doesn't hit the floating-point
    // precision issue that fractional-second conversions trigger.
    await Promise.all(plaques.map((entry, i) => (async () => {
      if (i > 0) {
        await new Promise((r) => setTimeout(r, i * interPlaqueDelayMs));
      }
      if (destroyed || token?.cancelled) return;
      try { gameAudio.playPlaquePop(i); } catch { /* non-fatal */ }
      const cancel = tripCancel(entry);
      await animateChoicePlaqueEntrance(entry.container, {
        cfg, cancel,
        phase1Ms: entrancePhase1Ms,
        phase2Ms: entrancePhase2Ms,
      });
    })()));
  }

  function onTap(cb) {
    if (typeof cb !== 'function') return () => {};
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  }

  function findEntry(plaqueId) {
    return plaques.find((p) => p.id === plaqueId) || null;
  }

  async function playCorrectAnimation(plaqueId) {
    const entry = findEntry(plaqueId);
    if (!entry || destroyed) return;
    const cancel = tripCancel(entry);
    await animateChoicePlaqueCorrect(entry.container, { cfg, cancel });
  }

  async function playIncorrectAnimation(plaqueId) {
    const entry = findEntry(plaqueId);
    if (!entry || destroyed) return;
    entry.selected = true;
    const cancel = tripCancel(entry);
    await animateChoicePlaqueIncorrect(entry.container, { cfg, cancel });
  }

  async function dimAllUnselected() {
    if (destroyed) return;
    const toDim = plaques.filter((p) => !p.selected);
    await Promise.all(toDim.map((entry) => {
      entry.selected = true;
      const cancel = tripCancel(entry);
      return animateChoicePlaqueIncorrect(entry.container, { cfg, cancel })
        .catch(() => { /* keep the group resolve healthy */ });
    }));
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    for (const entry of plaques) {
      if (entry.currentCancel) entry.currentCancel.cancelled = true;
      // Remove listeners before detaching from the layer to avoid a
      // late pointerup reaching a torn-down entry.
      for (const { event, handler } of entry.listeners) {
        try { entry.container.off?.(event, handler); } catch { /* no-op */ }
      }
      entry.listeners = [];
      try { layer.removeChild?.(entry.container); } catch { /* no-op */ }
      try { entry.container.destroy?.({ children: true }); } catch { /* no-op */ }
    }
    plaques.length = 0;
    subscribers.clear();
  }

  return {
    addPlaques,
    onTap,
    playCorrectAnimation,
    playIncorrectAnimation,
    dimAllUnselected,
    destroy,
    // Test hook for inspection.
    __getPlaques() {
      return plaques.map((p) => ({
        id: p.id, value: p.value, isCorrect: p.isCorrect, selected: p.selected,
      }));
    },
  };
}
